import { initDatePicker } from './date-picker.js';
import { clamp, getMonthDate } from './form-values.js';
import { t } from '../features/i18n/index.js';

const DEFAULT_OFFSET_MONTHS = 12;

export const RECALC_MONTH_SELECTOR = '#recalc-list .recalc-row input[data-field="month"]';

function listOf(form: HTMLFormElement): HTMLElement | null {
  return form.querySelector<HTMLElement>('#recalc-list');
}

function monthInputs(form: HTMLFormElement): HTMLInputElement[] {
  return Array.from(form.querySelectorAll<HTMLInputElement>(RECALC_MONTH_SELECTOR));
}

function buildRow(month: string): HTMLElement {
  const row = document.createElement('div');
  row.className = 'lump-row recalc-row';

  const label = document.createElement('label');
  label.className = 'field';
  const caption = document.createElement('span');
  caption.textContent = t('bank.recalculateMonth');
  const input = document.createElement('input');
  input.type = 'month';
  input.dataset.field = 'month';
  input.value = month;
  label.append(caption, input);

  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'table-btn lump-remove';
  remove.setAttribute('aria-label', t('bank.recalculateRemove'));
  remove.textContent = '×';

  row.append(label, remove);
  return row;
}

function syncCount(form: HTMLFormElement): void {
  const count = form.querySelector<HTMLElement>('#recalc-count');
  const total = monthInputs(form).length;
  if (count) count.textContent = total ? `· ${total}` : '';
}

/** Appends a row with a desktop date picker; returns it, or `null` without the list. */
export function addRecalculationRow(form: HTMLFormElement, month: string): HTMLElement | null {
  const list = listOf(form);
  if (!list) return null;
  const row = buildRow(month);
  list.appendChild(row);
  const input = row.querySelector<HTMLInputElement>('input');
  if (input) initDatePicker(input);
  syncCount(form);
  return row;
}

/** Months (YYYY-MM) of the first payment with a recalculated amount, in DOM order; blanks skipped. */
export function readRecalculations(form: HTMLFormElement): string[] {
  return monthInputs(form).map(input => input.value).filter(Boolean);
}

/** Replaces the rows; the spoiler stays as it is, its summary shows the count. */
export function setRecalculations(form: HTMLFormElement, months: string[]): void {
  const list = listOf(form);
  if (!list) return;
  list.replaceChildren();
  months.forEach(month => addRecalculationRow(form, month));
  syncCount(form);
}

/**
 * Month of the built-in example's recalculation: half-way through the term, as in the bank's
 * own schedule (payment 181 of 360). Without it the 31-day floor closes the loan years early.
 */
export function sampleRecalculation(form: HTMLFormElement): string {
  const startDate = (form.elements.namedItem('startDate') as HTMLInputElement | null)?.value || '2024-01';
  const termYears = parseFloat((form.elements.namedItem('termYears') as HTMLInputElement | null)?.value ?? '');
  return getMonthDate(startDate, Math.round(clamp(termYears, 1, 40) * 6));
}

/** A year after the first payment, clamped to the last month that still has a following payment. */
function defaultMonth(form: HTMLFormElement): string {
  const startDate = (form.elements.namedItem('startDate') as HTMLInputElement | null)?.value || '2024-01';
  const termYears = parseFloat((form.elements.namedItem('termYears') as HTMLInputElement | null)?.value ?? '');
  const termMonths = Math.max(2, Math.round(clamp(termYears, 1, 40) * 12));
  return getMonthDate(startDate, Math.min(DEFAULT_OFFSET_MONTHS, termMonths - 1));
}

/**
 * Wires «Плановые пересчёты платежа»: «+ Ещё пересчёт» appends a month, «×» removes one
 * (an empty list means no scheduled recalculation). `onChange` runs after every add or remove.
 */
export function initRecalculationList(form: HTMLFormElement, onChange: () => void): void {
  const list = listOf(form);
  if (!list) return;
  form.querySelector('#recalc-add')?.addEventListener('click', () => {
    addRecalculationRow(form, defaultMonth(form));
    onChange();
  });
  list.addEventListener('click', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const row = target.closest('.lump-remove')?.closest<HTMLElement>('.recalc-row');
    if (!row || !list.contains(row)) return;
    row.remove();
    syncCount(form);
    onChange();
  });
  syncCount(form);
}
